/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { type FrequencyModelDto } from '@treasury/api/channel';
import { Holiday, HolidaysService } from '@treasury/domain/dates';
import { EntitlementsService } from '@treasury/domain/entitlements';
import { TmApiError } from '@treasury/domain/shared';
import {
    CreditorsService,
    defaultFrequency,
    Wire,
    WireAccount,
    WireBeneficiary,
    WireCompany,
    WireConfiguration,
    WireCreditor,
    type WireIsoLabels,
    WireIsoService,
    WiresService,
    WireType,
} from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import { buildingIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/forms/tm-form';
import { TmValueChanged } from '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/tm-currency-field';
import {
    mapFrequencyModelDtoToFrequency,
    mapFrequencyToFrequencyModelDto,
} from '@treasury/presentation/components/tm-frequency.mappings';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-toggle-content';
import { toTitleCase } from '@treasury/utils';
import { format } from 'date-fns';
import { css, html, nothing } from 'lit';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from 'lit-vaadin-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import {
    DatePickerDate,
    inactiveHolidays,
    inactiveWeekends,
} from '../../../utilities/disabled-date.functions';
import { filterTableResults } from '../../../utilities/table-result-filter';
import '../creditors/partials/creditors-table';
import { ValidWireDateTime } from '../types/wire-valid-datetime.type';

export const tagName = 'wire-data-editable';
@customElement(tagName)
export class WireDataEditable extends TmContainer {
    @property({ type: Object })
    public frequency: FrequencyModelDto = defaultFrequency;

    @property({ type: Object })
    public wireConfiguration!: WireConfiguration;

    @property()
    public wireType = WireType.Domestic;

    @InjectProperty()
    declare private wireService: WiresService;

    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @InjectProperty()
    declare private holidaysService: HolidaysService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @DeepReactive()
    private wire?: Wire;

    @state()
    private hasWireIsoFeatureFlag = false;

    @state()
    private creditorAccounts: Array<WireBeneficiary> | Array<WireCreditor> = [];

    @state()
    private wireCompanies: Array<WireCompany> = [];

    @state()
    private wireCompanyOptions: Array<WireCompany> = [];

    @state()
    private filteredWireCompanies: Array<WireCompany> = [];

    @state()
    private debitAccounts: Array<WireAccount> = [];

    @state()
    private filteredDebitAccounts: Array<WireAccount> = [];

    @state()
    private domesticCreateCreditorEntitlement = false;

    @state()
    private internationalCreateCreditorEntitlement = false;

    @state()
    private amount: string | undefined;

    @state()
    private wireId?: number;

    @state()
    private holidays: Array<Holiday> = [];

    @state()
    private maxStartDate?: Date;

    @state()
    private validWireDateTime: ValidWireDateTime | undefined;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    public async firstUpdated() {
        if (this.wire) {
            this.wire.destinationCurrency = 'USD';
            if (this.wireType === WireType.International) {
                this.wire.isInternational = true;
            }
        }

        await this.tryFetch(
            async () =>
                Promise.all([
                    this.entitlementsService.getEntitlements(),
                    this.creditorsService.getBeneficiariesByType({
                        status: 'Ready',
                        beneficiaryType: toTitleCase(this.wireType),
                        requireEntitlement: false,
                        applicationNameType: 0,
                    }),
                    this.wireService.getWireCompanies(true),
                    this.wireService.checkValidDateTime(),
                    this.holidaysService.getHolidays(),
                    this.wireIsoService.getEntitlementWireISO20022(),
                    this.wireIsoService.getLabels(),
                    this.wireConfiguration
                        ? this.wireConfiguration
                        : (this.wireService.getWireConfiguration() as unknown as WireConfiguration),
                ]),
            ([
                entitlements,
                creditorAccounts,
                wireCompanies,
                validWireDateTime,
                holidays,
                hasWireIsoFeatureFlag,
                wireIsoLabels,
                wireConfiguration,
            ]) => {
                this.domesticCreateCreditorEntitlement =
                    entitlements.get('Wire, Domestic, Create Beneficiary') || false;
                this.internationalCreateCreditorEntitlement =
                    entitlements.get('Wire, International, Create Beneficiary') || false;
                this.creditorAccounts = (creditorAccounts as WireCreditor[]).filter(
                    c => c.isForeignCurrency === false
                );
                this.wireCompanies = wireCompanies;
                this.validWireDateTime = validWireDateTime;
                this.holidays = holidays;
                this.hasWireIsoFeatureFlag = hasWireIsoFeatureFlag;
                this.wireIsoLabels = wireIsoLabels;
                this.wireConfiguration = wireConfiguration;
                this.filteredWireCompanies = this.wireCompanies;

                if (this.wire && this.wireId) {
                    this.wire.beneficiary = this.creditorAccounts.find(
                        creditor => creditor.id === this.wireId
                    ) as WireCreditor;
                }

                if (
                    this.validWireDateTime.isAfterCutoff ||
                    this.validWireDateTime.isHoliday ||
                    this.validWireDateTime.isWeekend
                ) {
                    this.frequency.valueDate = format(this.validWireDateTime.nextBusinessDay, 'P');
                }

                if (this.wireConfiguration.maximumFutureDays) {
                    this.maxStartDate = new Date();
                    this.maxStartDate.setDate(
                        this.maxStartDate.getDate() +
                            (this.wireConfiguration.maximumFutureDays ?? 0)
                    );
                }
            }
        );
    }

    protected render() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return html`<tm-section>
            <tm-form
                @form-changed=${() => {
                    this.updateWire();
                }}
            >
                <tm-form-row .label=${this.wireIsoLabels.creditor} required noWrap>
                    <tm-slider
                        header=${`Select ${this.wireIsoLabels.creditor}`}
                        .value=${this.wire?.beneficiary.name
                            ? this.wire.beneficiary.name
                            : undefined}
                        .placeholder=${`Select ${this.wireIsoLabels.creditor}`}
                    >
                        ${this.renderAddCreditorButton()}
                        <creditors-table
                            .creditors=${this.creditorAccounts}
                            @select=${({ detail, target }: CustomEvent) => {
                                if (this.wire) {
                                    this.wire.beneficiary = detail;
                                }
                                target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                                this.requestUpdate();
                            }}
                        ></creditors-table>
                    </tm-slider>
                </tm-form-row>
                <tm-form-row .label=${'Wire company name'} required noWrap>
                    <tm-slider
                        header="Select Wire Company"
                        .value=${this.wire?.wireCompany?.name}
                        .placeholder=${'Select wire company'}
                    >
                        ${this.renderWireCompanies()}
                    </tm-slider>
                </tm-form-row>
                <tm-form-row .label=${'Debit account'} required noWrap>
                    <tm-slider
                        header="Select Debit Account"
                        .value=${this.wire?.debitAccount?.accountDisplayLabel
                            ? this.wire.debitAccount.accountDisplayLabel
                            : undefined}
                        .placeholder=${'Select debit account'}
                        .disabled=${!this.wire?.wireCompany}
                    >
                        ${this.renderDebitAccounts()}
                    </tm-slider>
                </tm-form-row>
                <tm-form-row label="Amount" required>
                    <tm-currency-field
                        class="max-w-[130px]"
                        .initial=${this.wire?.amount}
                        @value-changed=${(e: TmValueChanged<string>) => {
                            const { value } = e.detail;
                            if (!value || !this.wire) {
                                return;
                            }

                            this.amount = value;
                            this.wire.amount = parseFloat(this.amount);
                        }}
                    ></tm-currency-field>
                </tm-form-row>

                ${this.renderFrequency()}

                <tm-form-row label="Purpose" required .itemsStart=${true}>
                    <tm-text-field
                        .initial=${this.wire?.purpose}
                        .maxLength=${30}
                        .helperText="${`${this.wire?.purpose?.length ?? 0}/30`}"
                        @value-changed=${(e: CustomEvent) => {
                            if (this.wire) {
                                this.wire.purpose = e.detail.value;
                            }
                        }}
                    ></tm-text-field>
                </tm-form-row>

                <tm-toggle-content>
                    <tm-form-row label="Additional Information" .itemsStart=${true}>
                        <tm-multi-line-text-input
                            placeholder="Line"
                            .maxLines=${4}
                            .characterLimit=${35}
                            .pattern="${/^[a-zA-Z0-9!"#$%'()+/:;\\<=>?@_`^~\-.&, ]+$/}"
                            .value=${this.wire?.additionalInformation ?? []}
                            @change=${(e: CustomEvent) => {
                                if (this.wire) {
                                    this.wire.additionalInformation = e.detail;
                                }
                            }}
                        ></tm-multi-line-text-input>
                    </tm-form-row>

                    ${this.wireType !== WireType.International || this.hasWireIsoFeatureFlag
                        ? html`
                              <tm-form-row label=${this.wireIsoLabels.endToEndId} noWrap>
                                  <tm-text-field
                                      .initial=${this.wire?.referenceBeneficiary}
                                      .maxLength=${16}
                                      .helperText="${`${this.wire?.referenceBeneficiary?.length ?? 0}/16`}"
                                      @value-changed=${(e: CustomEvent) => {
                                          if (this.wire) {
                                              this.wire.referenceBeneficiary = e.detail.value;
                                          }
                                      }}
                                  ></tm-text-field>
                              </tm-form-row>
                          `
                        : nothing}
                </tm-toggle-content>
            </tm-form>
        </tm-section>`;
    }

    private renderAddCreditorButton() {
        if (
            (this.wireType === WireType.Domestic && this.domesticCreateCreditorEntitlement) ||
            (this.wireType === WireType.International &&
                this.internationalCreateCreditorEntitlement)
        ) {
            return html`<tm-action-button
                class="my-2"
                .disabled=${false}
                .buttonTitle=${`Add ${this.wireIsoLabels.creditor}`}
                @click=${() => {
                    this.dispatchEvent(
                        new CustomEvent('create-creditor', {
                            bubbles: true,
                            composed: true,
                        })
                    );
                    this.requestUpdate();
                }}
            ></tm-action-button>`;
        }
        return nothing;
    }

    private async getDebitAccounts() {
        this.loading = true;
        const type = this.wireType === WireType.Domestic ? 'createDomestic' : 'createIntl';
        try {
            if (this.wire?.wireCompany) {
                this.debitAccounts = await this.wireService.getDebitAccounts(
                    this.wire.wireCompany.id,
                    type
                );
                this.filteredDebitAccounts = this.debitAccounts;
            }
        } catch (error) {
            this.loading = false;
            this.notificationService.renderError(error as TmApiError);
        } finally {
            this.loading = false;
        }
    }

    private updateWire() {
        this.dispatchEvent(new CustomEvent('update', { detail: this.wire }));
    }

    private filterResults(e: CustomEvent, type: string) {
        switch (type) {
            case 'wireCompanies':
                this.filteredWireCompanies = filterTableResults(e, this.wireCompanies, ['name']);
                break;
            case 'debitAccounts':
                this.filteredDebitAccounts = filterTableResults(e, this.debitAccounts, [
                    'accountDisplayLabel',
                    'number',
                    'availableBalance',
                ]);
                break;
            default:
        }
    }

    private renderWireCompanies() {
        const renderAccount = (item: WireCompany) =>
            html`<div class="flex flex-row items-center">
                <tm-icon
                    .icon=${buildingIcon}
                    width="24px"
                    height="24px"
                    color="var(--tm-color-gray-medium)"
                ></tm-icon>
                <div class="ml-4 text-sm font-medium">${item.name}</div>
            </div>`;
        return html`<tm-section>
            <tm-list-header
                .filterButton=${false}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.filterResults(e, 'wireCompanies');
                }}
            ></tm-list-header>
            ${this.loading
                ? html`<tm-loader card></tm-loader>`
                : html`<vaadin-grid
                      .items=${this.filteredWireCompanies}
                      @active-item-changed=${(e: CustomEvent) => {
                          if (!e.detail.value) {
                              return;
                          }
                          if (this.wire) {
                              this.wire.wireCompany = e.detail.value;
                          }
                          this.getDebitAccounts();
                          e.target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                      }}
                      ><vaadin-grid-column
                          ${columnBodyRenderer(
                              renderAccount as GridColumnBodyLitRenderer<WireCompany>
                          )}
                      ></vaadin-grid-column
                  ></vaadin-grid>`}
        </tm-section>`;
    }

    private renderDebitAccounts() {
        const renderNameAndNumber = (item: WireAccount) =>
            html`<div class="flex justify-between">
                <div class="flex flex-col">
                    <div class="text-sm font-medium">${item.accountDisplayLabel}</div>
                    <div class="text-xs text-secondary">${item.number}</div>
                </div>
            </div>`;
        return html`<tm-section>
            <tm-list-header
                .filterButton=${false}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.filterResults(e, 'debitAccounts');
                }}
            ></tm-list-header>
            ${this.loading
                ? html`<tm-loader card></tm-loader>`
                : html`<vaadin-grid
                      .items=${this.filteredDebitAccounts}
                      @active-item-changed=${(e: CustomEvent) => {
                          if (!e.detail.value) {
                              return;
                          }
                          if (this.wire) {
                              this.wire.debitAccount = e.detail.value;
                          }
                          e.target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                          this.updateWire();
                          this.requestUpdate();
                      }}
                  >
                      <vaadin-grid-column
                          ${columnBodyRenderer(
                              renderNameAndNumber as GridColumnBodyLitRenderer<WireAccount>
                          )}
                      >
                      </vaadin-grid-column>
                  </vaadin-grid>`}
        </tm-section>`;
    }

    private renderFrequency() {
        if (this.wireType === WireType.International) {
            return html`<tm-form-row required noWrap .label=${'Effective date'} class="py-2">
                <div class="flex justify-end">${this.wire?.frequency?.valueDate}</div></tm-form-row
            >`;
        } else {
            return html`<tm-form-frequency
                required
                .loading=${this.loading}
                dateLabel="Frequency"
                .frequency=${this.frequency
                    ? mapFrequencyModelDtoToFrequency(this.frequency)
                    : undefined}
                .isDateDisabled=${(date: DatePickerDate) =>
                    inactiveWeekends(date) || inactiveHolidays(date, this.holidays)}
                .maxStartDate=${this.maxStartDate}
                @select-frequency=${(e: CustomEvent) => {
                    if (this.wire) {
                        this.wire.frequency = e.detail
                            ? (mapFrequencyToFrequencyModelDto(e.detail) as FrequencyModelDto)
                            : e.detail;
                    }
                }}
            ></tm-form-frequency>`;
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireDataEditable;
    }
}
